// src/components/editor/WordCountModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Grid,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotesIcon from '@mui/icons-material/Notes';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const WordCountModal = ({ open, onClose, editorRef, goals }) => {
  const [stats, setStats] = useState({
    words: 0,
    characters: 0,
    charactersNoSpaces: 0,
    sentences: 0,
    paragraphs: 0,
    pages: 0,
    readingTime: 0,
    speakingTime: 0,
  });

  useEffect(() => {
    if (open && editorRef?.current) {
      const text = editorRef.current.innerText || '';
      
      // Word count
      const words = text.trim().split(/\s+/).filter(w => w.length > 0);
      const wordCount = words.length;
      
      // Character counts
      const characters = text.length;
      const charactersNoSpaces = text.replace(/\s/g, '').length;
      
      // Sentence count (approximate)
      const sentences = (text.match(/[.!?]+/g) || []).length;

      // Paragraph count — real content blocks in the editor (fall back to a
      // whitespace-based estimate if the editor structure isn't available).
      const el = editorRef.current;
      const blocks = Array.from(el.querySelectorAll('.rv-page-body p, .rv-page-body li'))
        .filter((b) => !b.classList.contains('rv-end-spacer') && b.textContent.trim().length > 0);
      const paragraphs = blocks.length || text.split(/\n\n+/).filter(p => p.trim().length > 0).length || 1;

      // Page count — actual paginated pages (fall back to ~250 words/page).
      const realPages = el.querySelectorAll('.rv-page').length;
      const pages = realPages || Math.ceil(wordCount / 250);
      
      // Reading time (average 200-250 words per minute)
      const readingTime = Math.ceil(wordCount / 225);
      
      // Speaking time (average 125-150 words per minute)
      const speakingTime = Math.ceil(wordCount / 137);
      
      setStats({
        words: wordCount,
        characters,
        charactersNoSpaces,
        sentences,
        paragraphs,
        pages,
        readingTime,
        speakingTime,
      });
    }
  }, [open, editorRef]);

  // Calculate progress toward goals
  const wordGoal = goals?.wordCountGoal || 50000; // Default to 50k for a novel
  const wordProgress = Math.min((stats.words / wordGoal) * 100, 100);

  const StatCard = ({ icon, label, value, subtext }) => (
    <Box sx={{
      bgcolor: 'rgba(255,255,255,0.05)',
      borderRadius: 2,
      p: 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
    }}>
      {icon}
      <Typography variant="h4" sx={{ color: '#F5D76E', fontWeight: 700, my: 1 }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
      <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
        {label}
      </Typography>
      {subtext && (
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>
          {subtext}
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#2d2d44',
          color: 'white',
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotesIcon sx={{ color: '#F5D76E' }} />
          <Typography variant="h6">Word Count</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {/* Main stats grid */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <StatCard 
              icon={<TextFieldsIcon sx={{ color: '#F5D76E', fontSize: 32 }} />}
              label="Words"
              value={stats.words}
            />
          </Grid>
          <Grid item xs={6}>
            <StatCard 
              icon={<FormatListNumberedIcon sx={{ color: '#4fc3f7', fontSize: 32 }} />}
              label="Characters"
              value={stats.characters}
              subtext={`${stats.charactersNoSpaces.toLocaleString()} without spaces`}
            />
          </Grid>
          <Grid item xs={6}>
            <StatCard 
              icon={<MenuBookIcon sx={{ color: '#81c784', fontSize: 32 }} />}
              label="Pages"
              value={stats.pages}
              subtext="in document"
            />
          </Grid>
          <Grid item xs={6}>
            <StatCard 
              icon={<AccessTimeIcon sx={{ color: '#ffb74d', fontSize: 32 }} />}
              label="Reading Time"
              value={`${stats.readingTime} min`}
              subtext={`${stats.speakingTime} min speaking`}
            />
          </Grid>
        </Grid>
        
        {/* Additional stats */}
        <Box sx={{ 
          bgcolor: 'rgba(255,255,255,0.05)', 
          borderRadius: 2, 
          p: 2,
          mb: 3
        }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Sentences
              </Typography>
              <Typography variant="h6" sx={{ color: 'white' }}>
                {stats.sentences.toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Paragraphs
              </Typography>
              <Typography variant="h6" sx={{ color: 'white' }}>
                {stats.paragraphs.toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Avg Words/Sentence
              </Typography>
              <Typography variant="h6" sx={{ color: 'white' }}>
                {stats.sentences > 0 ? Math.round(stats.words / stats.sentences) : 0}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Avg Words/Paragraph
              </Typography>
              <Typography variant="h6" sx={{ color: 'white' }}>
                {stats.paragraphs > 0 ? Math.round(stats.words / stats.paragraphs) : 0}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        
        {/* Progress toward goal */}
        {wordGoal > 0 && (
          <Box sx={{ 
            bgcolor: 'rgba(245, 215, 110, 0.1)', 
            border: '1px solid rgba(245, 215, 110, 0.3)',
            borderRadius: 2, 
            p: 2 
          }}>
            <Typography variant="subtitle2" sx={{ color: '#F5D76E', mb: 1 }}>
              Progress Toward Goal
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={wordProgress}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: wordProgress >= 100 ? '#4caf50' : '#F5D76E',
                      borderRadius: 5,
                    }
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: 'white', minWidth: 50 }}>
                {wordProgress.toFixed(1)}%
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {stats.words.toLocaleString()} of {wordGoal.toLocaleString()} words
              {wordGoal - stats.words > 0 && (
                <> • {(wordGoal - stats.words).toLocaleString()} remaining</>
              )}
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', p: 2 }}>
        <Button 
          variant="contained"
          onClick={onClose}
          sx={{ bgcolor: '#F5D76E', color: '#2d2d44', '&:hover': { bgcolor: '#e6c757' } }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WordCountModal;
